import React, { useState, useMemo } from 'react';
import { useForm } from 'react-hook-form';

// Import UI Components with named imports
import { FormHeader } from './FormHeader';
import { FormActions } from './FormActions';
import FieldRenderer from './FieldRenderer';
import { ProgressBar } from './ProgressBar';
import { StatusMessage } from './StatusMessage';
import { StepIndicator } from './StepIndicator';

// Import Hooks with named import
import { useFormTheme } from './hooks/useFormTheme';

// Import Utils
import { getNestedValue } from './utils/formUtils';

export const DynamicForm = ({
  // Basic props
  fields = [],
  onSubmit: onFormSubmit,
  formTitle,
  formDescription,
  
  // Styling
  theme = 'default',
  className = '',
  
  // Layout configuration
  layout = {
    type: 'grid', // 'grid' | 'flex' | 'single'
    columns: 12,  // Number of columns for grid layout
    gap: 4,       // Gap between fields (Tailwind gap-x value)
    responsive: { // Responsive columns configuration
      sm: 1,
      md: 2,
      lg: 3,
      xl: 4
    }
  },
  
  // Legacy columns prop for backward compatibility
  columns,
  
  // Form behavior
  mode = 'onSubmit',
  defaultValues = {},
  resetOnSubmit = false,
  
  // Features
  showProgress = false,
  
  // Buttons
  submitText = 'Submit',
  resetText = 'Reset',
  showReset = true,
  
  // Hooks
  transformData,
  beforeSubmit,
  afterSubmit,
  onError,
  
  // Custom components
  customComponents = {},
  
  // Advanced
  debug = false
}) => {
  const [submitStatus, setSubmitStatus] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  
  const themeStyles = useFormTheme(theme);
  
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isDirty, isValid, dirtyFields },
    reset,
    control,
    watch,
    setValue,
    getValues,
    trigger,
    clearErrors
  } = useForm({
    mode,
    defaultValues
  });
  
  // Process layout configuration
  const processedLayout = useMemo(() => {
    // Handle legacy columns prop
    if (columns && !layout) {
      return {
        type: 'grid',
        columns: columns,
        gap: 4,
        responsive: null
      };
    }
    
    // Handle layout object
    if (typeof layout === 'object') {
      return {
        type: layout.type || 'grid',
        columns: layout.columns || 12,
        gap: layout.gap || 4,
        responsive: layout.responsive || null
      };
    }
    
    // Handle string layout type
    if (typeof layout === 'string') {
      return {
        type: layout,
        columns: 12,
        gap: 4,
        responsive: null
      };
    }
    
    // Default layout
    return {
      type: 'grid',
      columns: 12,
      gap: 4,
      responsive: null
    };
  }, [layout, columns]);
  
  // Get visible fields based on conditions
  const visibleFields = useMemo(() => {
    return fields.filter(field => {
      if (!field.condition) return true;
      
      const { field: conditionField, operator, value } = field.condition;
      const fieldValue = watch(conditionField);
      
      switch (operator) {
        case 'equals':
          return fieldValue === value;
        case 'notEquals':
          return fieldValue !== value;
        case 'contains':
          return fieldValue?.includes?.(value);
        case 'greaterThan':
          return Number(fieldValue) > value;
        case 'lessThan':
          return Number(fieldValue) < value;
        case 'notEmpty':
          return fieldValue !== '' && fieldValue !== null && fieldValue !== undefined;
        case 'isEmpty':
          return !fieldValue || fieldValue === '';
        default:
          return true;
      }
    });
  }, [fields, watch]);
  
  // Group fields by steps
  const steps = useMemo(() => {
    if (!visibleFields.some(f => f.step !== undefined)) {
      return [{
        title: null,
        description: null,
        fields: visibleFields
      }];
    }
    
    const grouped = {};
    visibleFields.forEach(field => {
      const step = field.step || 0;
      if (!grouped[step]) {
        grouped[step] = {
          title: field.stepTitle || `Step ${step + 1}`,
          description: field.stepDescription,
          fields: []
        };
      }
      grouped[step].fields.push(field);
    });
    
    const stepsArray = Object.values(grouped);
    return stepsArray.length > 0 ? stepsArray : [{
      title: null,
      description: null,
      fields: []
    }];
  }, [visibleFields]);
  
  const validCurrentStep = Math.min(currentStep, steps.length - 1);
  const currentStepData = steps[validCurrentStep] || { fields: [] };
  const currentFields = currentStepData.fields || [];
  
  // Calculate progress
  const progress = useMemo(() => {
    if (!showProgress) return 0;
    const totalFields = visibleFields.length;
    if (totalFields === 0) return 0;
    const filledFields = Object.keys(dirtyFields).length;
    return Math.round((filledFields / totalFields) * 100);
  }, [showProgress, visibleFields, dirtyFields]);
  
  // Form submission handler
  const handleFormSubmit = async (data) => {
    try {
      setSubmitStatus('loading');
      
      if (beforeSubmit) {
        const shouldContinue = await beforeSubmit(data);
        if (!shouldContinue) {
          setSubmitStatus(null);
          return;
        }
      }
      
      const finalData = transformData ? transformData(data) : data;
      
      if (onFormSubmit) {
        await onFormSubmit(finalData);
      }
      
      setSubmitStatus('success');
      
      if (afterSubmit) {
        await afterSubmit(finalData);
      }
      
      if (resetOnSubmit) {
        reset();
      }
      
      setTimeout(() => setSubmitStatus(null), 3000);
    } catch (error) {
      setSubmitStatus('error');
      if (onError) {
        onError(error);
      } else {
        console.error('Form submission error:', error);
      }
      setTimeout(() => setSubmitStatus(null), 5000);
    }
  };
  
  // Build layout classes based on configuration
  const getLayoutClasses = () => {
    const { type, columns, gap, responsive } = processedLayout;
    let classes = [];
    
    if (type === 'grid') {
      classes.push('grid');
      
      // Add responsive column classes
      if (responsive) {
        // Mobile first approach
        classes.push(`grid-cols-${responsive.sm || 1}`);
        if (responsive.md) classes.push(`md:grid-cols-${responsive.md}`);
        if (responsive.lg) classes.push(`lg:grid-cols-${responsive.lg}`);
        if (responsive.xl) classes.push(`xl:grid-cols-${responsive.xl}`);
        if (responsive['2xl']) classes.push(`2xl:grid-cols-${responsive['2xl']}`);
      } else {
        // Fixed columns
        classes.push(`grid-cols-${columns}`);
      }
      
      // Add gap
      classes.push(`gap-${gap}`);
    } else if (type === 'flex') {
      classes.push('flex flex-wrap');
      classes.push(`gap-${gap}`);
    }
    // 'single' type doesn't need special classes
    
    return classes.join(' ');
  };
  
  // Build inline styles for non-Tailwind usage
  const getLayoutStyles = () => {
    const { type, columns, gap } = processedLayout;
    
    if (!themeStyles.useTailwind) {
      if (type === 'grid') {
        return {
          display: 'grid',
          gridTemplateColumns: `repeat(${columns}, 1fr)`,
          gap: `${gap * 0.25}rem`
        };
      } else if (type === 'flex') {
        return {
          display: 'flex',
          flexWrap: 'wrap',
          gap: `${gap * 0.25}rem`
        };
      }
    }
    
    return {};
  };
  
  const layoutClasses = getLayoutClasses();
  const layoutStyles = getLayoutStyles();
  
  return (
    <div className={`${themeStyles.container} ${className}`}>
      <FormHeader 
        title={formTitle} 
        description={formDescription} 
        theme={themeStyles} 
      />
      
      <ProgressBar 
        progress={progress} 
        show={showProgress} 
      />
      
      <StepIndicator 
        steps={steps} 
        currentStep={validCurrentStep} 
      />
      
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <div 
          className={layoutClasses}
          style={layoutStyles}
        >
          {currentFields.map((field) => (
            <FieldRenderer
              key={field.id}
              field={field}
              register={register}
              control={control}
              errors={errors}
              watch={watch}
              setValue={setValue}
              getValues={getValues}
              theme={themeStyles}
              customComponents={customComponents}
            />
          ))}
        </div>
        
        <FormActions
          steps={steps}
          currentStep={validCurrentStep}
          setCurrentStep={setCurrentStep}
          isSubmitting={isSubmitting}
          submitStatus={submitStatus}
          submitText={submitText}
          resetText={resetText}
          showReset={showReset}
          onReset={() => reset()}
          theme={themeStyles}
        />
      </form>
      
      <StatusMessage 
        status={submitStatus} 
        theme={themeStyles} 
      />
      
      {debug && (
        <div className="mt-4 p-4 bg-gray-100 rounded">
          <pre className="text-xs">{JSON.stringify(watch(), null, 2)}</pre>
        </div>
      )}
    </div>
  );
};

export default DynamicForm;